import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import PropTypes from 'prop-types';

/**
 * StreamingMarkdown
 * Renders markdown with a "neat streaming" effect (word-boundary buffering)
 *
 * Why: prevents temporary half-words that look like spelling errors.
 */
const StreamingMarkdown = ({ content, speed = 35, onComplete }) => {
  const [displayedContent, setDisplayedContent] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  // Refs to avoid stale closures and reduce rerenders
  const bufferRef = useRef(''); // full target content
  const cursorRef = useRef(0); // where we are in content
  const pendingRef = useRef(''); // pending text not yet committed
  const lastFlushRef = useRef(0); // last UI update time
  const rafRef = useRef(null);
  const completedRef = useRef(false);

  // Characters we allow to end a “safe” flush
  const boundaryRegex = useMemo(() => /[\s\n]|[.,!?;:)\]]/g, []);

  useEffect(() => {
    if (!content) return;

    // Reset
    setDisplayedContent('');
    setIsComplete(false);
    completedRef.current = false;

    bufferRef.current = content;
    cursorRef.current = 0;
    pendingRef.current = '';
    lastFlushRef.current = performance.now();

    // Controls "typing speed"
    // speed here is ms between flushes (lower = faster)
    const flushEveryMs = Math.max(16, speed);

    const step = (now) => {
      if (completedRef.current) return;

      const full = bufferRef.current;
      const len = full.length;

      // Pull in a small chunk to pending buffer each frame.
      // (Chunk size can be tiny; we only FLUSH at boundaries.)
      if (cursorRef.current < len) {
        const take = Math.min(12, len - cursorRef.current);
        pendingRef.current += full.slice(cursorRef.current, cursorRef.current + take);
        cursorRef.current += take;
      }

      // Throttle UI flushes
      const shouldFlushByTime = now - lastFlushRef.current >= flushEveryMs;

      // Flush only when we have a safe boundary (space/newline/punct)
      if (shouldFlushByTime && pendingRef.current.length > 0) {
        // Find last safe boundary in pending
        let lastBoundaryIndex = -1;
        let match;
        boundaryRegex.lastIndex = 0;

        while ((match = boundaryRegex.exec(pendingRef.current)) !== null) {
          lastBoundaryIndex = match.index;
        }

        if (lastBoundaryIndex !== -1) {
          const commit = pendingRef.current.slice(0, lastBoundaryIndex + 1);
          const remainder = pendingRef.current.slice(lastBoundaryIndex + 1);

          setDisplayedContent((prev) => prev + commit);
          pendingRef.current = remainder;
          lastFlushRef.current = now;
        } else {
          // If no boundary appears for a while (very long token),
          // flush a small amount to avoid "stalling".
          if (pendingRef.current.length > 60) {
            const commit = pendingRef.current.slice(0, 30);
            pendingRef.current = pendingRef.current.slice(30);
            setDisplayedContent((prev) => prev + commit);
            lastFlushRef.current = now;
          }
        }
      }

      // Completion: flush remainder and end
      if (cursorRef.current >= len) {
        // Flush any remaining pending
        if (pendingRef.current.length > 0) {
          setDisplayedContent((prev) => prev + pendingRef.current);
          pendingRef.current = '';
        }

        completedRef.current = true;
        setIsComplete(true);
        if (onComplete) onComplete();
        return;
      }

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [content, speed, onComplete, boundaryRegex]);

  return (
    <div className="streaming-markdown">
      <ReactMarkdown
        components={{
          p: ({ children }) => <p className="markdown-paragraph">{children}</p>,
          h1: ({ children }) => <h1 className="markdown-h1">{children}</h1>,
          h2: ({ children }) => <h2 className="markdown-h2">{children}</h2>,
          h3: ({ children }) => <h3 className="markdown-h3">{children}</h3>,
          ul: ({ children }) => <ul className="markdown-list">{children}</ul>,
          ol: ({ children }) => <ol className="markdown-ordered-list">{children}</ol>,
          li: ({ children }) => <li className="markdown-list-item">{children}</li>,
          code: ({ inline, children }) =>
            inline ? (
              <code className="markdown-inline-code">{children}</code>
            ) : (
              <pre className="markdown-code-block">
                <code>{children}</code>
              </pre>
            ),
          blockquote: ({ children }) => (
            <blockquote className="markdown-blockquote">{children}</blockquote>
          ),
          a: ({ href, children }) => (
            <a href={href} className="markdown-link" target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          strong: ({ children }) => <strong className="markdown-bold">{children}</strong>,
          em: ({ children }) => <em className="markdown-italic">{children}</em>,
        }}
      >
        {displayedContent}
      </ReactMarkdown>

      {!isComplete && <span className="streaming-cursor">▊</span>}
    </div>
  );
};

StreamingMarkdown.propTypes = {
  content: PropTypes.string.isRequired,
  speed: PropTypes.number, // ms between flushes; try 25–60
  onComplete: PropTypes.func,
};

export default StreamingMarkdown;
